import type { JobAssignment, StatusChangeLog } from '@/lib/schemas/db'
import type { User } from '@/db/domain/models'
import { statusIsBlocking } from '@/db/domain/statusRules'

/** Fields shared by JobAssignment and ServiceAssignment. */
export interface AssignmentLike {
  userId: string
  assignedAt?: string
  unassignedAt?: string
  createdAt: string
}

export interface CurrentAssignment<T extends AssignmentLike> {
  assignment: T
  user?: User
}

export function getCurrentAssignment<T extends AssignmentLike>(
  assignments: T[],
  usersById?: Map<string, User>,
): CurrentAssignment<T> | undefined {
  const active = assignments
    .filter((assignment) => !assignment.unassignedAt)
    .sort((a, b) => {
      const aTime = a.assignedAt ?? a.createdAt
      const bTime = b.assignedAt ?? b.createdAt
      return bTime.localeCompare(aTime)
    })[0]

  if (!active) return undefined

  return {
    assignment: active,
    user: usersById?.get(active.userId),
  }
}

export type CurrentJobAssignment = CurrentAssignment<JobAssignment>

export function getCurrentJobAssignment(
  assignments: JobAssignment[],
  usersById?: Map<string, User>,
): CurrentJobAssignment | undefined {
  return getCurrentAssignment(assignments, usersById)
}

export function formatAssignedUser(user?: Pick<User, 'name' | 'email'>) {
  if (!user) return 'Unassigned'
  return user.name?.trim() || user.email?.trim() || 'Assigned'
}

/**
 * Latest reason captured on a transition into `status`, if any.
 *
 * Ordering is by createdAt alone. Two entries written inside the same
 * millisecond tie, and the winner between them is then arbitrary — callers pass
 * collection order, which is keyed by random id and carries no chronology, so
 * there is no sound tie-break available here. Reason updates are human-paced in
 * practice, so a tie needs two writes in one millisecond to bite.
 */
function getLatestReasonForStatus(status: string, statusChanges?: StatusChangeLog[]) {
  const latest = [...(statusChanges ?? [])]
    .filter((change) => change.newStatus === status)
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0]

  return latest?.reason?.trim() || undefined
}

/**
 * Why the work is stalled — the reason captured on the move into whichever
 * blocking status it currently sits in. Looked up by the live status rather
 * than the literal 'Blocked', so a job waiting on parts shows its parts list
 * instead of an older, unrelated blocked reason.
 */
export function getCurrentBlockedReason(status: string, statusChanges?: StatusChangeLog[]) {
  if (!statusIsBlocking(status)) return undefined
  return getLatestReasonForStatus(status, statusChanges)
}

/** Why the repair was unsuccessful — the reason captured on the move to collect. */
export function getCurrentUnsuccessfulReason(
  status: string,
  statusChanges?: StatusChangeLog[],
) {
  if (status !== 'ReadyForCollectionUnsuccessful') return undefined
  return getLatestReasonForStatus('ReadyForCollectionUnsuccessful', statusChanges)
}
